import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/layout/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import EmployeeManager from "./pages/EmployeeManager";
import CustomerManager from "./pages/CustomerManager";
import LeadsManager from "./pages/LeadsManager";
import ContractsManager from "./pages/ContractsManager";
import LicensesManager from "./pages/LicensesManager";
import ServiceManager from "./pages/ServiceManager";
import EngineerDashboard from "./pages/EngineerDashboard";
import InvoiceManager from "./pages/InvoiceManager"; // Keep import for potential future use
import SalesMarketingDashboard from "./pages/SalesMarketingDashboard";
import RolesMaster from "./pages/RolesMaster";
import AttendanceManager from "./pages/AttendanceManager";
import AttendanceSettings from "./pages/AttendanceSettings";
import BusinessSettings from "./pages/BusinessSettings";
import BrandMaster from "./pages/BrandMaster";
import CategoryMaster from "./pages/CategoryMaster";
import TaxSettings from "./pages/TaxSettings";
import SupplierMaster from "./pages/SupplierMaster";
import ProductMaster from "./pages/ProductMaster";
import PurchaseEntry from "./pages/PurchaseEntry";
import InventoryList from "./pages/InventoryList";
import ExpenseHeaders from "./pages/ExpenseHeaders";
import ExpensesManager from "./pages/ExpensesManager";
import SupplierPayments from "./pages/SupplierPayments";
import SupplierLedger from "./pages/SupplierLedger";
import OwnerDashboard from "./pages/OwnerDashboard";
import BillingSettings from "./pages/BillingSettings";
import ComplaintForm from "./pages/ComplaintForm";
import ComplaintsManager from "./pages/ComplaintsManager";
import BackupSettings from "./pages/BackupSettings";
import ServiceApprovalManager from "./pages/ServiceApprovalManager";
import SalesBillingManager from "./pages/SalesBillingManager";
import CreateBill from "./pages/CreateBill";
import CreateQuotation from "./pages/CreateQuotation";
import PaymentCollections from "./pages/PaymentCollections";
import CustomerLedger from "./pages/CustomerLedger";
import SerialNumberTracker from "./pages/SerialNumberTracker";
import SalesReturn from "./pages/SalesReturn";
import MobileApp from "./mobile/MobileApp";
import ReportsModule from "./pages/ReportsModule";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/complaint" element={<ComplaintForm />} />
          
          {/* Mobile App Route */}
          <Route path="/mobile/*" element={<MobileApp />} />
          
          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route
              path="owner-dashboard"
              element={
                <ProtectedRoute requiredPermission="owner_dashboard" allowedRoles={["Admin"]}>
                  <OwnerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <ReportsModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="engineer-dashboard"
              element={
                <ProtectedRoute requiredPermission="service_management" allowedRoles={["Service Engineer"]}>
                  <EngineerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="sales-dashboard"
              element={
                <ProtectedRoute requiredPermission="leads" allowedRoles={["Sales & Marketing"]}>
                  <SalesMarketingDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="employees"
              element={
                <ProtectedRoute requiredPermission="employees" allowedRoles={["Admin"]}>
                  <EmployeeManager />
                </ProtectedRoute>
              }
            />
            {/* Settings Routes */}
            <Route
              path="settings/roles"
              element={
                <ProtectedRoute requiredPermission="roles_master" allowedRoles={["Admin"]}>
                  <RolesMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/employees"
              element={
                <ProtectedRoute requiredPermission="employees" allowedRoles={["Admin"]}>
                  <EmployeeManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="customers"
              element={
                <ProtectedRoute requiredPermission="customers" allowedRoles={["Admin", "Back Office Executive", "Backoffice Accounts Executive", "BDM & SE"]}>
                  <CustomerManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="leads"
              element={
                <ProtectedRoute requiredPermission="leads" allowedRoles={["Admin", "Back Office Executive", "Backoffice Accounts Executive", "Sales & Marketing", "BDM & SE"]}>
                  <LeadsManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="services"
              element={
                <ProtectedRoute requiredPermission="service_management" allowedRoles={["Admin", "Service Engineer", "Back Office Executive", "Backoffice Accounts Executive", "BDM & SE"]}>
                  <ServiceManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="complaints"
              element={
                <ProtectedRoute requiredPermission="complaints" allowedRoles={["Admin", "Back Office Executive", "Backoffice Accounts Executive", "BDM & SE"]}>
                  <ComplaintsManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="contracts"
              element={
                <ProtectedRoute requiredPermission="contract_management" allowedRoles={["Admin", "Back Office Executive", "Backoffice Accounts Executive"]}>
                  <ContractsManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="licenses"
              element={
                <ProtectedRoute requiredPermission="licenses" allowedRoles={["Admin", "Back Office Executive", "Backoffice Accounts Executive"]}>
                  <LicensesManager />
                </ProtectedRoute>
              }
            />
            {/* Invoices module removed - handled in Sales module */}
            <Route
              path="attendance"
              element={
                <ProtectedRoute requiredPermission="attendance" allowedRoles={["Admin", "Back Office Executive", "Backoffice Accounts Executive"]}>
                  <AttendanceManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/attendance"
              element={
                <ProtectedRoute requiredPermission="attendance_settings" allowedRoles={["Admin"]}>
                  <AttendanceSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/business"
              element={
                <ProtectedRoute requiredPermission="business_settings" allowedRoles={["Admin"]}>
                  <BusinessSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/brands"
              element={
                <ProtectedRoute requiredPermission="brand_master" allowedRoles={["Admin", "Back Office Executive"]}>
                  <BrandMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/categories"
              element={
                <ProtectedRoute requiredPermission="category_master" allowedRoles={["Admin", "Back Office Executive"]}>
                  <CategoryMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/taxes"
              element={
                <ProtectedRoute requiredPermission="tax_settings" allowedRoles={["Admin"]}>
                  <TaxSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/suppliers"
              element={
                <ProtectedRoute requiredPermission="supplier_master" allowedRoles={["Admin"]}>
                  <SupplierMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/products"
              element={
                <ProtectedRoute requiredPermission="product_master" allowedRoles={["Admin", "Back Office Executive"]}>
                  <ProductMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="inventory/purchases"
              element={
                <ProtectedRoute requiredPermission="purchase_entry" allowedRoles={["Admin", "Back Office Executive", "Backoffice Accounts Executive"]}>
                  <PurchaseEntry />
                </ProtectedRoute>
              }
            />
            <Route
              path="inventory/stock"
              element={
                <ProtectedRoute requiredPermission="stock_list" allowedRoles={["Admin", "Back Office Executive", "Backoffice Accounts Executive"]}>
                  <InventoryList />
                </ProtectedRoute>
              }
            />
            <Route
              path="inventory/serial-tracker"
              element={
                <ProtectedRoute requiredPermission="serial_tracker" allowedRoles={["Admin", "Back Office Executive", "Backoffice Accounts Executive"]}>
                  <SerialNumberTracker />
                </ProtectedRoute>
              }
            />
            <Route
              path="expenses"
              element={
                <ProtectedRoute requiredPermission="expenses" allowedRoles={["Admin", "Back Office Executive", "Backoffice Accounts Executive"]}>
                  <ExpensesManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/expense-headers"
              element={
                <ProtectedRoute requiredPermission="expense_headers" allowedRoles={["Admin"]}>
                  <ExpenseHeaders />
                </ProtectedRoute>
              }
            />
            <Route
              path="supplier-payments"
              element={
                <ProtectedRoute requiredPermission="supplier_payments" allowedRoles={["Admin", "Back Office Executive"]}>
                  <SupplierPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="supplier-ledger"
              element={
                <ProtectedRoute requiredPermission="supplier_ledger" allowedRoles={["Admin", "Back Office Executive"]}>
                  <SupplierLedger />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/billing"
              element={
                <ProtectedRoute requiredPermission="business_settings" allowedRoles={["Admin"]}>
                  <BillingSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/backup"
              element={
                <ProtectedRoute requiredPermission="business_settings" allowedRoles={["Admin"]}>
                  <BackupSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="service-approvals"
              element={
                <ProtectedRoute requiredPermission="service_approvals" allowedRoles={["Admin", "Backoffice Accounts Executive"]}>
                  <ServiceApprovalManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="sales-billing"
              element={
                <ProtectedRoute requiredPermission="sales_billing" allowedRoles={["Admin", "Backoffice Accounts Executive"]}>
                  <SalesBillingManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="sales-billing/create-bill"
              element={
                <ProtectedRoute requiredPermission="sales_billing" allowedRoles={["Admin", "Backoffice Accounts Executive"]}>
                  <CreateBill />
                </ProtectedRoute>
              }
            />
            <Route
              path="sales-billing/create-quotation"
              element={
                <ProtectedRoute requiredPermission="sales_billing" allowedRoles={["Admin", "Backoffice Accounts Executive"]}>
                  <CreateQuotation />
                </ProtectedRoute>
              }
            />
            <Route
              path="payment-collections"
              element={
                <ProtectedRoute requiredPermission="sales_billing" allowedRoles={["Admin", "Backoffice Accounts Executive"]}>
                  <PaymentCollections />
                </ProtectedRoute>
              }
            />
            <Route
              path="customer-ledger"
              element={
                <ProtectedRoute requiredPermission="sales_billing" allowedRoles={["Admin", "Backoffice Accounts Executive"]}>
                  <CustomerLedger />
                </ProtectedRoute>
              }
            />
            <Route
              path="sales-returns"
              element={
                <ProtectedRoute requiredPermission="sales_billing" allowedRoles={["Admin", "Backoffice Accounts Executive"]}>
                  <SalesReturn />
                </ProtectedRoute>
              }
            />
            <Route
              path="payments"
              element={
                <ProtectedRoute requiredPermission="sales_manager" allowedRoles={["Admin"]}>
                  <div className="text-center py-20">
                    <h2 className="text-2xl font-bold">Payment Management</h2>
                    <p className="text-gray-600 mt-2">Coming in Phase 5</p>
                  </div>
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
